import React, { useState } from "react";
import { useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import ComputerIcon from '@material-ui/icons/Computer';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import TabletIcon from '@material-ui/icons/Tablet';
import DevicesIcon from '@material-ui/icons/Devices';
import trustedDeviceIllustration from "../../../utils/images/illustration-trusted-device.svg";
import { FormattedMessage } from "react-intl";
import FieldSkeleton from "../../../components/skeleton/FieldSkeleton";
import IframeDialog from "../../../components/iframeDialog/iframeDialog";


/**
 * @function TrustedDevices
 * @description Functional component for managing trusted devices section
 */
const TrustedDevices = ({onChanges}) => {
    const user = useSelector(state => state.userManager);
    const trustedDevices = useSelector(state => state.userManager?.identity?.trusted_devices);
    const identity = useSelector(state => state.userManager?.identity);
    const applicationId = useSelector(state => state.auth?.profile?.azp);
    const federation_origin = useSelector(state => state.auth?.profile?.federation_origin);
    // Helper function to sort trusted devices - current device first, limit to 3
    const sortAndLimitTrustedDevices = (devices) => {        
        // Find current device and other devices
        const currentDevice = devices?.find(device => device?.current);
        const otherDevices = devices?.filter(device => !device?.current);
        
        // Create sorted array with current device first, then others
        const sortedDevices = currentDevice ? [currentDevice, ...otherDevices] : devices;
        
        // Limit to 3 devices
        return sortedDevices?.slice(0, 3);
    };
    // Process the trusted devices to ensure current device is first and limit to 3
    const trusted_devices = sortAndLimitTrustedDevices(trustedDevices);
    const userRegion = useSelector(state => {
        const regionEndpoints = {
            'us': process.env.REACT_APP_US_IDENTITY_ENDPOINT,
            'eu': process.env.REACT_APP_EU_IDENTITY_ENDPOINT,
            'uk': process.env.REACT_APP_UK_IDENTITY_ENDPOINT,
            'eu-gb': process.env.REACT_APP_UK_IDENTITY_ENDPOINT,
            'ap-au': process.env.REACT_APP_AU_IDENTITY_ENDPOINT
        };
        return regionEndpoints[state.auth?.profile?.data_region ?? "us"];
    });
    
    // State for dialog management
    const [frameUrl, setFrameUrl] = useState(null);
    
    // State for toast notification
    const [showToast, setShowToast] = useState(false);

    // Function to determine device type from browser and OS
    const getDeviceType = (browser, os) => {
        const osLower = os ? os.toLowerCase() : '';
        // Mobile phones
        if (
            (osLower.includes('android') && !osLower.includes('tablet')) ||
            osLower.includes('iphone') || 
            osLower.includes('ipod') || 
            osLower.includes('ios')
        ) {
            return 'mobile';
        }
        // Tablets
        if (
            osLower.includes('ipad') || 
            osLower.includes('tablet') || 
            osLower.includes('android')
        ) {
            return 'tablet';
        }
        // Desktops and laptops
        if (
            osLower.includes('windows') ||
            osLower.includes('mac') ||
            osLower.includes('linux') ||
            osLower.includes('ubuntu') ||
            osLower.includes('chrome os')
        ) {
            return 'desktop';
        }
        return 'devices';
    };

    // Function to get device icon based on type (matching iframe logic)
    const getDeviceIcon = (browser, os) => {
        const deviceType = getDeviceType(browser, os);
        switch(deviceType) {
            case 'mobile':
                return <SmartphoneIcon />;
            case 'tablet':
                return <TabletIcon />;
            case 'desktop':
                return <ComputerIcon />;
            default:
                return <DevicesIcon />;
        }
    };

    // Helper function to get frame URL with params
    const getFrameUrl = (frameUrl) => {
        const preferredUsername = user?.identity?.preferred_username || user?.identity?.email;
        const locale = user?.language;
        
        let params = {
            username: preferredUsername,
            application: applicationId,
            ui_locales: locale,
            federation_origin: federation_origin,
            origin: window.location.origin
        };
        
        const queryComponents = Object.keys(params).reduce((agg, val) => {
            if (typeof params[val] === "string" && params[val]?.length > 0) {
                agg.push(`${val}=${encodeURIComponent(params[val])}`)
            }
            return agg
        }, []);
        
        return queryComponents.length > 0 ? `${frameUrl}?${queryComponents.join("&")}` : frameUrl;
    };

    // Open dialog with iframe
    const openManageDevicesDialog = () => {
        const deviceTrustUrl = `${userRegion}/ui/device_trust.html`;
        const encodedUrl = getFrameUrl(deviceTrustUrl);
        setFrameUrl(encodedUrl);
    };

    // Close dialog
    const closeDialog = (data) => {
        // Handle any data returned from iframe if needed
        if (data?.trusted_devices_changed && onChanges) {
            // Refresh trusted devices data
            onChanges();
        }
        
        // Show toast if all devices were revoked
        if (data?.revoked_all_devices === true) {
            setShowToast(true);
        }
        
        setFrameUrl(null);
    };

    // Close toast notification
    const closeToast = () => {
        setShowToast(false);
    };

    // Render skeleton loading state while data is being fetched
    const renderLoadingSkeleton = () => (
            <div style={{ marginBottom: '16px' }}>
                <FieldSkeleton />
            </div>
    );

    // Render empty state when no trusted devices
    const renderEmptyState = () => (
        <div className="trusted-devices-content empty-state">
            <div className="illustration-container">
                <img 
                    src={trustedDeviceIllustration} 
                    alt="Trusted device illustration"
                    className="trusted-device-icon"
                />
            </div>
            <div className="content-section">
                <Typography variant="h5" className="section-title">
                    <FormattedMessage defaultMessage="Trust a device for faster sign-in" />
                </Typography>
                <Typography variant="body1" component="div">
                    <FormattedMessage defaultMessage='Check the "Remember this device" checkbox on your next sign-in.' />
                </Typography>
            </div>
        </div>
    );

    // Render device list when trusted devices exist
    const renderDeviceList = () => (
        <div className="trusted-devices-content device-list">
            <Typography variant="body1" className="manage-description">
                <FormattedMessage defaultMessage="Manage devices you trust for faster sign-in." />
            </Typography>
            <div className="devices-container">
                {trusted_devices.map((device, index) => (
                    <div key={index} className="device-item">
                        <div className="device-icon">
                            {getDeviceIcon(device.browser, device.os)}
                        </div>
                            <FormattedMessage 
                                defaultMessage="{browser} on {os}" 
                                values={{
                                    browser: device?.browser,
                                    os: device?.os
                                }} 
                            />
                            {device?.current && (
                                <span className="custom-badge-green"><FormattedMessage defaultMessage="Current device"/></span>
                            )}
                    </div>
                ))}
            </div>
            {trustedDevices?.length > 3 && (
                <div className="manage-all-devices-text">
                    <FormattedMessage defaultMessage="You're signed in on more devices." />
                </div>
            )}
            <Button 
                variant="text" 
                className="manage-all-devices-button custom-button buttonLink custom-button__focus"
                onClick={openManageDevicesDialog}
                disableFocusRipple
            >
                <FormattedMessage defaultMessage="Manage all devices" />
            </Button>
        </div>
    );

    // If identity exists but trusted_devices or email attribute is not present, don't show the section
   if (!!identity && ((!trustedDevices?.length > 0 ) || !identity?.email)) {
        return null;
   }

    return (
        <>
            <div className="card trusted-devices">
                {trustedDevices && (
                <div className="card-header">
                    <Typography variant="h3">
                        <FormattedMessage defaultMessage="Trusted devices" />
                    </Typography>
                </div>
                )}
                
                {!identity ? renderLoadingSkeleton() : 
                trusted_devices?.length > 0 ? renderDeviceList() : renderEmptyState()}
            </div>
                <IframeDialog
                open={!!frameUrl}
                frameUrl={frameUrl}
                onClose={closeDialog}
                iframeId="trusted-devices-frame"
                title="Manage Trusted Devices"
            />
            
            <Snackbar
                anchorOrigin={{ horizontal: "center", vertical: "top" }}
                open={showToast}
                onClose={closeToast}
                autoHideDuration={6000}
                className="snackbar"
            >
                <MuiAlert elevation={6} variant="filled" severity="success" onClose={closeToast}>
                    <FormattedMessage defaultMessage="All trusted devices have been forgotten." />
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default TrustedDevices;